/** A link an answer cites: echo keeps it as rN and sends the ones the answer uses */
export type MiloRef = { kind: string; path: string; label: string };

/** milo's events (echo's chat/events.ts); readers ignore types they do not know */
export type MiloEvent =
  | { type: 'start'; messageId: string; conversationId: string }
  | { type: 'thinking'; id: string; delta: string }
  | { type: 'text'; id: string; delta: string }
  | { type: 'tool'; name: string; input?: unknown }
  | { type: 'refs'; refs: Record<string, MiloRef> }
  | { type: 'limit'; remainingToday: number; longChat?: boolean }
  | { type: 'error'; message: string }
  | { type: 'done' };

const KNOWN = new Set(['start', 'thinking', 'text', 'tool', 'refs', 'limit', 'error', 'done']);

/** One `data:` line as an event; null for anything else (comments, events the panel does not know) */
function eventOf(line: string): MiloEvent | null {
  if (!line.startsWith('data:')) return null;
  const data = line.slice(5).trim();
  if (data === '') return null;
  try {
    const event = JSON.parse(data) as { type?: unknown };
    return typeof event.type === 'string' && KNOWN.has(event.type) ? (event as MiloEvent) : null;
  } catch {
    return null;
  }
}

/** The answer's server-sent events as events, in order. A chunk may split a line, or a letter, anywhere */
export async function* readMiloStream(body: ReadableStream<Uint8Array>): AsyncGenerator<MiloEvent> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      // stream: a letter split between two chunks waits for its second half
      buffer += decoder.decode(value, { stream: true });
      let newline = buffer.indexOf('\n');
      while (newline >= 0) {
        const event = eventOf(buffer.slice(0, newline).replace(/\r$/, ''));
        buffer = buffer.slice(newline + 1);
        if (event) yield event;
        newline = buffer.indexOf('\n');
      }
    }
    const last = eventOf((buffer + decoder.decode()).replace(/\r$/, ''));
    if (last) yield last;
  } finally {
    reader.releaseLock();
  }
}
